/**
 * StaffPortalLeaveService
 * Service for staff leave operations in the portal.
 * Route: api/staff-portal/leaves
 */
import { StaffPortalBaseService } from './StaffPortalBaseService';
/**
 * StaffPortalLeaveService
 * Handles leave operations for the staff portal.
 * Maps to /api/staff-portal/leaves endpoint
 */
export class StaffPortalLeaveService extends StaffPortalBaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? StaffPortalLeaveService.DEFAULT_SERVICE_PATH,
            basePath: config?.basePath ?? StaffPortalLeaveService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== LIST OPERATIONS ====================
    /**
     * Get all leave requests for the current staff member
     * GET /api/staff-portal/leaves
     */
    async getMyLeaves() {
        const response = await this.getMyLeavesRaw();
        return response.data ?? [];
    }
    /**
     * Get all leaves (raw response)
     */
    getMyLeavesRaw() {
        return this.get();
    }
    // ==================== GET SINGLE LEAVE ====================
    /**
     * Get a specific leave request by ID
     * GET /api/staff-portal/leaves/{leaveId}
     */
    async getLeave(leaveId) {
        const response = await this.getLeaveRaw(leaveId);
        if (!response.data) {
            throw new Error(`Leave ${leaveId} not found`);
        }
        return response.data;
    }
    /**
     * Get leave (raw response)
     */
    getLeaveRaw(leaveId) {
        return this.get(String(leaveId));
    }
    // ==================== CREATE OPERATIONS ====================
    /**
     * Create a new leave request
     * POST /api/staff-portal/leaves
     */
    async createLeave(data) {
        const response = await this.createLeaveRaw(data);
        if (!response.data) {
            throw new Error('Failed to create leave request');
        }
        return response.data;
    }
    /**
     * Create leave (raw response)
     */
    createLeaveRaw(data) {
        return this.post('', data);
    }
    // ==================== UPDATE OPERATIONS ====================
    /**
     * Update a pending leave request
     * PUT /api/staff-portal/leaves/{leaveId}
     */
    async updateLeave(leaveId, data) {
        const response = await this.updateLeaveRaw(leaveId, data);
        if (!response.data) {
            throw new Error('Failed to update leave request');
        }
        return response.data;
    }
    /**
     * Update leave (raw response)
     */
    updateLeaveRaw(leaveId, data) {
        return this.put(String(leaveId), data);
    }
    // ==================== CANCEL OPERATIONS ====================
    /**
     * Cancel a pending leave request (sets StatusID to -2)
     * PATCH /api/staff-portal/leaves/{leaveId}/cancel
     */
    async cancelLeave(leaveId) {
        const response = await this.cancelLeaveRaw(leaveId);
        if (!response.data) {
            throw new Error('Failed to cancel leave request');
        }
        return response.data;
    }
    /**
     * Cancel leave (raw response)
     */
    cancelLeaveRaw(leaveId) {
        return this.patch(`${leaveId}/cancel`);
    }
    // ==================== APPROVAL ASKS ====================
    /**
     * Requests from the approver on one of the caller's own leaves (duty cover or a question),
     * oldest first; the open one has `isOpen=true`. 403 for a leave that isn't yours.
     * GET /api/staff-portal/leaves/{leaveId}/approval-asks
     */
    async getApprovalAsks(leaveId) {
        const response = await this.getApprovalAsksRaw(leaveId);
        return response.data ?? [];
    }
    /**
     * Get approval asks (raw response)
     */
    getApprovalAsksRaw(leaveId) {
        return this.get(this.getEndpoint('approvalAsks', `${leaveId}/approval-asks`));
    }
    /**
     * Answer the approver and send the leave back to them. Returns the updated leave (with `approvalChain`).
     * - Duty cover ask: add at least one leave duty first (StaffPortalLeaveDutyService), or send
     *   `noCoverNeeded=true` with a note in `responseText`; otherwise 400.
     * - Question: `responseText` is required (400 otherwise).
     * - 403 for a leave that isn't yours; 409 for a closed ask or the wrong askId.
     * PUT /api/staff-portal/leaves/{leaveId}/approval-asks/{askId}/respond
     */
    async respondToApprovalAsk(leaveId, askId, body) {
        const response = await this.respondToApprovalAskRaw(leaveId, askId, body);
        if (!response.data) {
            throw new Error('Failed to send your answer');
        }
        return response.data;
    }
    /**
     * Respond to an approval ask (raw response)
     */
    respondToApprovalAskRaw(leaveId, askId, body) {
        return this.put(this.getEndpoint('respondToApprovalAsk', `${leaveId}/approval-asks/${askId}/respond`), body ?? {});
    }
    // ==================== LEAVE BALANCES ====================
    /**
     * Get the authenticated staff member's PaySpace leave balances.
     * Lives at a different base path (`api/staff-portal/leave-balances`)
     * so we bypass `buildUrl` and call the absolute path directly.
     * GET /api/staff-portal/leave-balances
     */
    async getMyLeaveBalances(refresh = false) {
        const url = refresh
            ? '/hr/api/staff-portal/leave-balances?refresh=true'
            : '/hr/api/staff-portal/leave-balances';
        const response = await this.http.get(url);
        if (!response.data) {
            throw new Error('Failed to fetch leave balances');
        }
        return response.data;
    }
}
StaffPortalLeaveService.DEFAULT_SERVICE_PATH = 'hr';
StaffPortalLeaveService.BASE_PATH = 'api/staff-portal/leaves';
//# sourceMappingURL=StaffPortalLeaveService.js.map